import React, { useMemo } from 'react';

export interface Breakpoint {
  minWidth: number;
  columns: number;
  /** Optional gap for this breakpoint and above until another gap is set. */
  gap?: string | number;
}

export interface GridProps {
  children: React.ReactNode;
  breakpoints: Breakpoint[];
  /** Default gap when a breakpoint does not define its own. */
  gap?: string | number;
  className?: string;
}

const toGapCss = (gap: string | number): string =>
  typeof gap === 'number' ? `${gap}px` : gap;

const createGridId = (breakpoints: Breakpoint[], gap: string) => {
  const input = `${gap}:${breakpoints
    .map(({ minWidth, columns, gap: bpGap }) => {
      const gapPart = bpGap === undefined ? '' : `-${toGapCss(bpGap)}`;
      return `${minWidth}-${columns}${gapPart}`;
    })
    .join(':')}`;
  let hash = 5381;

  for (let index = 0; index < input.length; index += 1) {
    hash = (hash * 33) ^ input.charCodeAt(index);
  }

  return `responsive-grid-${(hash >>> 0).toString(36)}`;
};

export const ResponsiveGrid: React.FC<GridProps> = ({
  children,
  breakpoints,
  gap = '1.5rem',
  className = '',
}) => {
  const gapValue = toGapCss(gap);
  const gridId = useMemo(
    () => createGridId(breakpoints, gapValue),
    [breakpoints, gapValue],
  );
  // Generate media query CSS with unique ID to avoid conflicts between multiple grids
  const styleContent = useMemo(() => {
    const sorted = [...breakpoints].sort((a, b) => a.minWidth - b.minWidth);
    const firstBreakpoint = sorted[0];
    const initialColumns = firstBreakpoint?.columns || 1;
    const initialGap =
      firstBreakpoint?.gap !== undefined
        ? toGapCss(firstBreakpoint.gap)
        : gapValue;
    const baseSelector = `.${gridId}`;
    const mediaQueries = sorted
      .map((bp) => {
        const gapRule =
          bp.gap !== undefined ? ` gap: ${toGapCss(bp.gap)};` : '';
        return `@media (min-width: ${bp.minWidth}px) { ${baseSelector} { grid-template-columns: repeat(${bp.columns}, 1fr);${gapRule} } }`;
      })
      .join('\n');
    return `${baseSelector} { display: grid; gap: ${initialGap}; grid-template-columns: repeat(${initialColumns}, 1fr); }\n${mediaQueries}`;
  }, [breakpoints, gapValue, gridId]);

  return (
    <>
      <style>{styleContent}</style>
      <div className={`responsive-grid-wrapper ${gridId} ${className}`.trim()}>
        {children}
      </div>
    </>
  );
};

export default ResponsiveGrid;
